import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TreeInteractor, TreeState } from './TreeInteractor';
import { usePersistor } from '../persistence/PersistorContext';
import { versionCheckInteractor } from '../version/versionCheck';
import { BLANK_TREE_DATA, Entitlement } from '@skill-trees/shared';
import {
	TreeInteractorContext,
	type TreeInteractorContextValue,
} from './treeInteractorContextInstance';

/** Pristine pre-load state; also applied whenever treeId changes so stale data never shows. */
const BLANK_TREE_STATE: TreeState = {
	tree: BLANK_TREE_DATA,
	isDraft: false,
	error: undefined,
	privateAccessDenied: false,
	isLoading: true,
	entitlementLevel: Entitlement.None,
	canEdit: false,
	canUndo: false,
	canRedo: false,
	persistenceError: undefined
};

/**
 * `checkVersionOnLoad` exists for embedded viewers: the build-version check fetches
 * `/version.json` from whatever origin hosts the page, which in an embed is the host's
 * site, not ours. The failure is harmless but it is a stray request on every host page.
 */
export const TreeInteractorProvider: React.FC<{
	children: React.ReactNode;
	isDevMode?: boolean;
	treeId: string;
	checkVersionOnLoad?: boolean;
}> = ({ children, isDevMode = false, treeId, checkVersionOnLoad = true }) => {
	const persistor = usePersistor();
	
	const interactor = useMemo(() => {
		return new TreeInteractor(BLANK_TREE_DATA, persistor, treeId, isDevMode);
	}, [persistor, treeId, isDevMode]);

	// Reset treeState when treeId changes (when interactor changes)
	const [treeState, setTreeState] = useState<TreeState>(BLANK_TREE_STATE);

	useEffect(() => {
		// Reset state immediately when treeId changes to prevent showing stale data
		setTreeState(BLANK_TREE_STATE);

		// Set up the callback to update state when interactor data changes
		interactor.setUpdateCallback((state: TreeState) => {
			setTreeState(prevState => ({
				...state,
				isLoading: prevState.isLoading ?? false,
				entitlementLevel: state.entitlementLevel ?? prevState.entitlementLevel ?? Entitlement.None,
				canEdit: state.canEdit ?? prevState.canEdit ?? false,
				privateAccessDenied: state.privateAccessDenied ?? prevState.privateAccessDenied ?? false
			}));
		});
		
		const loadInitialData = async () => {
			try {
				const result = await interactor.loadInitialData();

				// Tree load is one of the version-check triggers (throttled internally).
				if (checkVersionOnLoad) {
					void versionCheckInteractor.check();
				}

				if (!result.success) {
					if (result.errorType === 'permission') {
						setTreeState(prevState => ({
							...prevState,
							privateAccessDenied: true,
							error: undefined,
							isLoading: false,
							entitlementLevel: Entitlement.None,
							canEdit: false
						}));
						return;
					}
					setTreeState(prevState => ({
						...prevState,
						error: result.error,
						privateAccessDenied: false,
						isLoading: false,
						entitlementLevel: Entitlement.None,
						canEdit: false
					}));
					return;
				}

				setTreeState({
					tree: result.data.treeData,
					isDraft: interactor.isDraft,
					error: undefined,
					privateAccessDenied: false,
					isLoading: false,
					entitlementLevel: result.data.entitlementLevel,
					canEdit: result.data.canEdit,
					canUndo: interactor.canUndo,
					canRedo: interactor.canRedo
				});
			} catch (error) {
				console.error('Failed to load initial data:', error);
				const errorMessage = error instanceof Error ? error.message : 'Failed to load skill tree data';
				setTreeState(prevState => ({
					...prevState,
					error: errorMessage,
					privateAccessDenied: false,
					isLoading: false,
					entitlementLevel: Entitlement.None,
					canEdit: false
				}));
			}
		};
		
		loadInitialData();

		// Cleanup: clear the callback when the interactor is replaced
		return () => {
			interactor.setUpdateCallback(() => {});
		};
	}, [interactor, checkVersionOnLoad]);

	return (
		<TreeInteractorContext.Provider value={{ interactor, treeState }}>
			{children}
		</TreeInteractorContext.Provider>
	);
};

export function useTreeInteractor(): TreeInteractor {
	const ctx = useContext(TreeInteractorContext);
	if (!ctx) {
		throw new Error('useTreeInteractor must be used within a TreeInteractorProvider');
	}
	return ctx.interactor;
}

export function useTreeInteractorContext(): TreeInteractorContextValue {
	const ctx = useContext(TreeInteractorContext);
	if (!ctx) {
		throw new Error('useTreeInteractorContext must be used within a TreeInteractorProvider');
	}
	return ctx;
}

export function useTreeState(): TreeState {
	const ctx = useContext(TreeInteractorContext);
	if (!ctx) {
		throw new Error('useTreeState must be used within a TreeInteractorProvider');
	}
	return ctx.treeState;
}

export type { TreeInteractorContextValue };
